import { memo } from "react";
import React from "react";
import { cn } from "../../lib/utils";
import type { MenuOrNavOption } from "./MobileNavBar";
import {
  ChevronRightIcon,
  ExternalLinkBlackIcon,
  ExternalLinkIcon,
  ChevronLeftIcon,
} from "./SMBIcons";

type NavItem = {
  nav: MenuOrNavOption;
  active: boolean;
  isInsidePartner: boolean;
  INDUSTRY_PACKAGE_EXTERNAL_PARTNER: string[];
  onMenuOnClick: (
    nav: MenuOrNavOption,
    e: React.MouseEvent<HTMLButtonElement>
  ) => void;
  onMenuKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement>,
    nav: MenuOrNavOption
  ) => void;
  pathname: string;
  idx: number;
  currentMenu: MenuOrNavOption[];
  tabIndex?: number;
};

const ActiveIndication = memo(({ active }: { active: boolean }) => (
  <div
    className="w-1 h-12 inline"
    style={{ backgroundColor: active ? "#235AE4" : "transparent" }}
    aria-hidden="true"
    role="presentation"
    id="navigation-menu-active-indicator"
  ></div>
));
ActiveIndication.displayName = "ActiveIndication";

const NavSpanElement = memo(
  ({
    title,
    className,
    style,
    ...props
  }: {
    title: string;
    className?: string;
    style?: React.CSSProperties;
  }) => {
    return (
      <span className={className} style={style} {...props}>
        {title}
      </span>
    );
  }
);
NavSpanElement.displayName = "NavSpanElement";

export const MobileNavBarButton = memo((props: NavItem) => {
  const {
    nav,
    active,
    isInsidePartner,
    INDUSTRY_PACKAGE_EXTERNAL_PARTNER,
    onMenuOnClick,
    onMenuKeyDown,
    pathname,
    idx,
    currentMenu,
    tabIndex = 0,
  } = props;

  // Dynamic ARIA label for screen readers
  const ariaLabel = nav?.children ? `${nav.title}, opens subMenu ` : nav?.title;

  return (
    <button
      key={nav?.title}
      onClick={(e) => onMenuOnClick(nav, e)}
      onKeyDown={onMenuKeyDown ? (e) => onMenuKeyDown(e, nav) : undefined}
      className={cn(
        "menu-button w-full flex justify-start items-center bg-white text-left",
        "focus-visible:outline focus",
        "focus-visible:bg-[#E6F0FF]",
        "focus:outline-blue focus:!border-blue focus-visible:!border-blue focus-visible:border-2 !text-[#2D2D3E] focus-visible:z-10"
      )}
      style={{
        color: "#2E2E32",
        border: idx !== currentMenu.length - 1 ? "1px solid #CCCCD2" : "none",
      }}
      aria-label={ariaLabel}
      role="menuitem"
      tabIndex={tabIndex}
      aria-current={active ? "page" : undefined}
      id="navigation-menu-item-button"
    >
      <ActiveIndication active={active} />
      <NavSpanElement
        title={nav?.title}
        className={cn("text-sm font-normal ml-2 tracing-[0.14px] menu-span")}
        style={{ color: active ? "#235AE4" : "#2E2E32" }}
      />
      {INDUSTRY_PACKAGE_EXTERNAL_PARTNER.includes(
        nav?.industryPackageCode ?? ""
      ) && nav?.isExternalLink ? (
        pathname.toLowerCase() === (nav.cta1URL?.toLowerCase() ?? "") ||
        isInsidePartner ? (
          <ExternalLinkIcon className="ml-2 mr-2 h-6 w-4 navigation-menu-item" />
        ) : (
          <ExternalLinkBlackIcon className="ml-2 mr-2 h-6 w-4 navigation-menu-item" />
        )
      ) : null}
      {nav?.children && (
        <ChevronRightIcon
          className=" ml-auto mr-2 h-6 w-4 navigation-menu-item"
          aria-hidden="true"
        />
      )}
    </button>
  );
});
MobileNavBarButton.displayName = "MobileNavBarButton";

export const MobileNavBarTitle = memo(({ title }: { title: string }) => {
  return (
    <div
      className="w-full  flex justify-start items-center  border-y border-[#CCCCD2] w-full "
      id="navigation-menu-title"
      style={{ color: "#2E2E32" }}
    >
      <ActiveIndication active={false} />
      <NavSpanElement
        title={title}
        className="ml-2 text-base font-medium tracing-[0.16px]"
        style={{ color: "#2E2E32" }}
      />
    </div>
  );
});
MobileNavBarTitle.displayName = "MobileNavBarTitle";

const handleReturnToHomeKeyDown = (
  e: React.KeyboardEvent<HTMLButtonElement>,
  onGoHome: () => void,
  onMenuKeyDown?: (
    e: React.KeyboardEvent<HTMLButtonElement>,
    nav: MenuOrNavOption
  ) => void,
  returnTitle?: string
) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    onGoHome();
  } else if (onMenuKeyDown) {
    onMenuKeyDown(e, { title: returnTitle || "Return to main menu" } as MenuOrNavOption); // for handling reset of focus
  }
};

export const MobileNavReturnToHome = memo(
  ({
    onGoHome,
    onMenuKeyDown,
    tabIndex,
    returnTitle,
  }: {
    onGoHome: () => void;
    onMenuKeyDown?: (
      e: React.KeyboardEvent<HTMLButtonElement>,
      nav: MenuOrNavOption
    ) => void;
    tabIndex?: number;
    returnTitle?: string;
  }) => {
    return (
      <button
        onClick={onGoHome}
        onKeyDown={(e) => handleReturnToHomeKeyDown(e, onGoHome, onMenuKeyDown, returnTitle)}
        aria-label="Go back"
        className="menu-button w-full flex justify-start items-center bg-white  px-[3px] font-normal text-sm"
        style={{ color: "#2E2E32" }}
        id="navigation-menu-return-button"
        role="menuitem"
        tabIndex={tabIndex}
      >
        <ActiveIndication active={false} />
        <ChevronLeftIcon
          className="ml-2 navigation-menu-return-button-icon"
          width={"10px"}
          height={"10px"}
        />
        <NavSpanElement
          title={returnTitle || " Return to main menu"}
          className="font-base text-sm  tracing-[0.14px] ml-2 navigation-menu-return-button-text"
        />
      </button>
    );
  }
);
MobileNavReturnToHome.displayName = "MobileNavReturnToHome";
